<template>
    <ion-input
        ref="input"
        class="mei-input mei-note-input"
        :class="{ 'ion-invalid': (errors?.length ?? 0) > 0, 'ion-margin-top': space, borderless: !border }"
        :label="label"
        v-model="value"
        :type="type"
        :disabled="disabled"
        :readonly="readonly"
        :error-text="errors ?? ''"
        @ionChange="changeHandler"
        @ionInput="validate"
        @ionBlur="markTouched"
        label-placement="stacked"></ion-input>
</template>

<script setup lang="ts">
import { InputChangeEventDetail, IonInput } from "@ionic/vue";
import { computed, PropType, ref } from "vue";
import { FormInputType } from "@/types/inputType";

const props = defineProps({
    type: {
        type: String as PropType<FormInputType>,
        required: false,
        default: FormInputType.text,
    },
    modelValue: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    readonly: {
        type: Boolean,
        required: false,
        default: false,
    },
    onTap: {
        type: Function as PropType<() => void>,
        required: false,
    },
    rules: {
        type: Array as PropType<Array<(value: any) => null | string>>,
        required: false,
        default: [],
    },
    valid: {
        type: Boolean,
        required: false,
    },
    space: {
        type: Boolean,
        required: false,
        default: true,
    },
    border: {
        type: Boolean,
        required: false,
        default: true,
    },
});
const emit = defineEmits(["update:modelValue", "update:valid"]);

const input = ref<HTMLInputElement | null>(null);
const value = computed({
    get() {
        return props.modelValue;
    },
    set(value) {
        emit("update:modelValue", value);
    },
});
const errors = computed(() => {
    return validate()?.join(", ");
});

const validate = (): string[] | null => {
    const errors: string[] = [];
    for (const rule of props.rules) {
        const validation = rule(value.value);
        if (validation !== null) {
            errors.push(validation);
        }
    }
    if (errors.length > 0) return errors;
    else return null;
};

const changeHandler = (ev?: InputChangeEventDetail) => {
    // Prefer event detail value when provided by IonInput
    // Fall back to current computed value otherwise
    // Never mutate value.value directly here to avoid accidental clears
    // and to keep v-model as the single source of truth
    // @ts-ignore - IonInput event typing
    const next = ev?.detail?.value ?? value.value;
    emit("update:modelValue", next);
    const validation = validate();
    emit("update:valid", !validation);
};

const markTouched = () => {
    // @ts-ignore
    input.value?.$el.classList.add("ion-touched");
};

const onBlur = () => {
    if (!props.readonly) {
        markTouched();
    }
    if (props.onTap) {
        props.onTap();
    }
};

defineExpose({ validate });
</script>

<style scoped>
.borderless {
    --border-width: 0;
}
</style>
